import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import api from "../../services/api";

const formatRoles = (roles) => {
  if (Array.isArray(roles)) {
    return roles
      .flatMap((role) => {
        if (Array.isArray(role)) return role;
        if (role && typeof role === "object") {
          return role.nome || role.name || role.title || "";
        }
        return role;
      })
      .map((role) => String(role).trim())
      .filter(Boolean)
      .join(", ");
  }

  if (typeof roles === "string") {
    const trimmed = roles.trim();
    if (!trimmed) return "-";

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return formatRoles(parsed);
      }
    } catch (error) {
      // ignora parsing e usa fallback abaixo
    }

    return trimmed
      .replace(/[\\[\]{}]/g, "")
      .split(",")
      .map((item) => item.trim().replace(/['"]/g, ""))
      .filter(Boolean)
      .join(", ") || "-";
  }

  return "-";
};

export default function ModalEditBudget({ isOpen, onClose, onSaved }) {
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");

  const carregarColaboradores = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/budget");
      setColaboradores(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Erro ao buscar colaboradores do orçamento:", err);
      setError("Não foi possível carregar os colaboradores.");
      setColaboradores([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      carregarColaboradores();
    }
  }, [isOpen]);

  const handleValorChange = (idColaborador, value) => {
    setColaboradores((prev) =>
      prev.map((item) =>
        Number(item.id_colaborador) === Number(idColaborador)
          ? { ...item, valor_estimado: value }
          : item
      )
    );
  };

  const salvarValor = async (colaborador) => {
    const valor = Number(colaborador.valor_estimado);

    if (!Number.isFinite(valor)) {
      setError("Informe um valor numérico válido para o colaborador.");
      return;
    }

    setSavingId(Number(colaborador.id_colaborador));
    setError("");

    try {
      await api.patch(`/budget/${colaborador.id_colaborador}`, {
        valor_estimado: valor,
      });

      if (onSaved) {
        onSaved();
      }
    } catch (err) {
      console.error("Erro ao atualizar valor do colaborador:", err);
      setError(err.response?.data?.error || "Não foi possível salvar o valor do colaborador.");
    } finally {
      setSavingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          zIndex: 1200,
        }}
        onClick={onClose}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 980,
            maxHeight: "88vh",
            overflowY: "auto",
            background: "#FFFFFF",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 20px 40px rgba(15, 23, 42, 0.25)",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 22, color: "#111827" }}>
                Editar valor por hora
              </h2>
              <div style={{ marginTop: 6, fontSize: 13, color: "#6B7280" }}>
                Atualize o valor por hora de cada colaborador.
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                border: "none",
                background: "transparent",
                fontSize: 28,
                color: "#6B7280",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          {error && (
            <div
              style={{
                background: "#FEE2E2",
                color: "#B91C1C",
                border: "1px solid #FCA5A5",
                borderRadius: 8,
                padding: "10px 12px",
                marginBottom: 16,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ padding: "18px 12px", color: "#6B7280" }}>Carregando colaboradores...</div>
          ) : colaboradores.length === 0 ? (
            <div style={{ padding: "18px 12px", color: "#6B7280" }}>
              Nenhum colaborador encontrado.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ background: "#F3F4F6" }}>
                  <tr>
                    <th style={thStyle}>Colaborador</th>
                    <th style={thStyle}>Usuário</th>
                    <th style={thStyle}>Cargo/Função</th>
                    <th style={thStyle}>Valor por hora</th>
                    <th style={thStyle}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {colaboradores.map((colaborador) => (
                    <tr key={colaborador.id_colaborador} style={{ background: "#FFFFFF" }}>
                      <td style={tdStyle}>{colaborador.nome_colaborador || "-"}</td>
                      <td style={tdStyle}>{colaborador.usuario_colaborador || "-"}</td>
                      <td style={tdStyle}>{formatRoles(colaborador.roles)}</td>
                      <td style={tdStyle}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={colaborador.valor_estimado ?? 0}
                          onChange={(event) =>
                            handleValorChange(colaborador.id_colaborador, event.target.value)
                          }
                          style={inputStyle}
                        />
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => salvarValor(colaborador)}
                          disabled={savingId === Number(colaborador.id_colaborador)}
                          style={buttonStyle}
                        >
                          {savingId === Number(colaborador.id_colaborador) ? "Salvando..." : "Salvar"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} style={secondaryButtonStyle}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "12px 14px",
  color: "#374151",
  borderBottom: "1px solid #E5E7EB",
  fontSize: 13,
  fontWeight: 700,
};

const tdStyle = {
  padding: "12px 14px",
  borderBottom: "1px solid #E5E7EB",
  color: "#111827",
  fontSize: 14,
  verticalAlign: "middle",
};

const inputStyle = {
  width: "120px",
  height: 36,
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 14,
  color: "#111827",
  background: "#FFFFFF",
};

const buttonStyle = {
  background: "#2563EB",
  color: "#FFFFFF",
  border: "none",
  borderRadius: 8,
  padding: "8px 12px",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  background: "#F3F4F6",
  color: "#111827",
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  padding: "9px 16px",
  fontWeight: 600,
  cursor: "pointer",
};
